import React, { useEffect, useState } from "react";
import { X, Upload } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchProviderPriority,
  fetchCountry,
  getProviderDetailById,
  addOrUpdateProviderImage,
} from "../../redux/slices/ProvidergalSlice";

const EditServiceModal = ({ isOpen, onClose, providerId }) => {
  const dispatch = useDispatch();
  const { providerPriolist, countryList, providerlistById } = useSelector(
    (state) => state.providerGallery
  );

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    image: null,
    old_image: "",
    country_id: "",
    priority: "",
    name: "",
    provider_image_gallery_id: "",
  });

  // Fetch priority & countries
  useEffect(() => {
    dispatch(fetchProviderPriority());
    dispatch(fetchCountry());
  }, [dispatch]);

  // Fetch provider detail when modal opens
  useEffect(() => {
    if (isOpen && providerId) {
      dispatch(getProviderDetailById(providerId));
    }
  }, [isOpen, providerId, dispatch]);

  // Set form when provider details are loaded
  useEffect(() => {
    if (providerlistById && providerlistById.id === providerId) {
      setForm({
        image: null,
        old_image: providerlistById.image || "",
        country_id: String(providerlistById.country_id || ""),
        priority: String(providerlistById.priority || ""),
        name: providerlistById.name || "",
        provider_image_gallery_id: providerlistById.id || "",
      });
    }
  }, [providerlistById]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setForm((prev) => ({ ...prev, image: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.country_id || !form.priority) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    const formData = new FormData();

    if (form.provider_image_gallery_id) {
      formData.append(
        "provider_image_gallery_id",
        form.provider_image_gallery_id
      );
    }

    formData.append("name", form.name);
    formData.append("priority", form.priority);
    formData.append("country_id", form.country_id);

    // NEW FIXED LOGIC
    if (form.image) {
      // user uploaded new image
      formData.append("image", form.image);
    } else {
      // user did not upload new image → send null
      formData.append("image", "");
    }

    setLoading(true);

    try {
      const res = await dispatch(addOrUpdateProviderImage(formData)).unwrap();

      Swal.fire("Success", "Updated successfully", "success");
      onClose();
    } catch (err) {
      console.error("Update error:", err);
      Swal.fire("Error", err.message || "Update failed", "error");
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"></div>

      {/* Modal */}
      <div className="fixed inset-0 flex justify-center items-center z-50 px-4">
        <div className="bg-white w-full max-w-2xl rounded-xl shadow-lg">
          {/* Header */}
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-800">
              {providerId ? "Edit Service Image" : "Add New Service Image"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={22} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Priority */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Select Priority
              </label>
              <select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                className="p-3 border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300 cursor-pointer"
              >
                <option value="">Select Priority</option>
                {providerPriolist?.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* Country */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Country Selection
              </label>
              <select
                name="country_id"
                value={form.country_id}
                onChange={handleChange}
                className="p-3 border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300 cursor-pointer"
              >
                <option value="">Select country</option>
                {countryList?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Image Name or Number
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. 'Main Service Shot' or '001'"
                className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>

            {/* Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center flex flex-col justify-center items-center hover:border-orange-400 transition relative">
              <Upload size={40} className="text-gray-400 mb-3" />
              <input
                type="file"
                name="image"
                id="serviceImage"
                className="hidden"
                onChange={handleChange}
              />
              <label
                htmlFor="serviceImage"
                className="text-sm text-orange-500 font-medium cursor-pointer hover:underline"
              >
                Click to upload
              </label>

              {/* Image Preview */}
              {(form.image || form.old_image) && (
                <img
                  src={
                    form.image
                      ? URL.createObjectURL(form.image)
                      : form.old_image
                  }
                  alt={form.image.name}
                  className="mt-3 w-24 h-24 object-cover mx-auto"
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t px-6 py-4">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
            >
              cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 rounded-md bg-orange-500 text-white flex items-center gap-2"
            >
              {loading && (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {loading ? "" : "Update"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditServiceModal;
